import { computed, Directive, inject, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CartService } from '@core/services/cart/cart.service';
import { TranslateService } from '@ngx-translate/core';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { PrepaymentSelectorService } from '@common/selectors/prepayment.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { CurrencyPipe } from '@angular/common';
import {
  formatMarketplacePrice,
  getMarketplaceCurrencySymbol,
} from '@shared/utils/currency/format-marketplace-price.util';

export type TopUpEvent =
  | {
      type: 'Top up added';
    }
  | {
      type: 'Top up removed';
    }
  | {
      type: 'Cancelled';
    };

@Directive({ standalone: true, providers: [CurrencyPipe] })
export class TopUpDirective {
  private readonly cartService = inject(CartService);
  private readonly translateService = inject(TranslateService);
  private readonly cartStoreSelector = inject(CartStoreSelector);
  private readonly prepaymentSelectorService = inject(PrepaymentSelectorService);
  private readonly marketplaceStoreSelector = inject(MarketplaceStoreSelector);
  private readonly currencyPipe = inject(CurrencyPipe);

  // Get marketplace currency as signal
  public readonly marketplaceCurrency = toSignal(
    this.marketplaceStoreSelector.selectMarketplaceCurrency,
    { initialValue: 'EUR' },
  );

  // Get currency symbol from marketplace
  public readonly currencySymbol = computed(() => {
    const currency = this.marketplaceCurrency();
    return getMarketplaceCurrencySymbol(currency);
  });

  public readonly topUpEvents = output<TopUpEvent>();

  public readonly inputValue = signal<number | null>(null);

  public readonly preSelectedAmountInCents = computed(() => {
    return this.cartStoreSelector.topUpProductInCart()?.amount;
  });

  public readonly selectedAmountInCents = computed(() => {
    return signal<number | undefined>(this.preSelectedAmountInCents() ?? 0);
  });

  public readonly isAmountValid = computed(() => {
    const amount = this.inputValue() ?? 0;

    if (amount === null || amount === 0) return true; // Pas d'erreur si vide
    const minAmount = this.minTopUpAmount() ?? 0;
    const maxAmount = this.maxTopUpAmount() ?? 0;
    return amount >= minAmount && amount <= maxAmount;
  });

  public readonly errorMessage = computed(() => {
    const amount = this.inputValue() ?? 0;
    if (amount === null || amount === 0 || this.isAmountValid()) return null;

    const minAmount = this.minTopUpAmount() ?? 0;
    const maxAmount = this.maxTopUpAmount() ?? 0;
    const currency = this.marketplaceCurrency();

    if (amount < minAmount) {
      const formattedMin = formatMarketplacePrice(minAmount, currency, {
        amountInCents: true,
        currencyPipe: this.currencyPipe,
      });
      return this.translateService.instant('prepaid-account-top-up-modal.minimum-amount-error', {
        amount: formattedMin,
      });
    }
    if (amount > maxAmount) {
      const formattedMax = formatMarketplacePrice(maxAmount, currency, {
        amountInCents: true,
        currencyPipe: this.currencyPipe,
      });
      return this.translateService.instant('prepaid-account-top-up-modal.maximum-amount-error', {
        amount: formattedMax,
      });
    }
    return null;
  });

  public readonly topUpPreselectedAmountInCents =
    this.prepaymentSelectorService.selectTopUpPreselectedAmountInCents;

  public readonly minTopUpAmount = this.prepaymentSelectorService.selectTopUpAmountMinInCents;

  public readonly maxTopUpAmount = this.prepaymentSelectorService.selectTopUpAmountMaxInCents;

  public readonly hasSelectedAPresetAmount = computed(() => {
    const selectedAmount = this.selectedAmountInCents()();
    const topUpPreselectedAmountInCents = this.topUpPreselectedAmountInCents();
    if (!selectedAmount) {
      return false;
    }
    return topUpPreselectedAmountInCents.includes(selectedAmount);
  });

  public onSelectedAmount(amountInCents?: number): void {
    this.selectedAmountInCents().set(amountInCents);
    this.inputValue.set(null);
  }

  public onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseFloat(target.value);

    if (isNaN(value) || target.value === '') {
      this.inputValue.set(null);
      this.selectedAmountInCents().set(0);
    } else {
      this.inputValue.set(value * 100);
      this.selectedAmountInCents().set(value * 100);
    }
  }

  public onKeyDown(event: KeyboardEvent, amount: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSelectedAmount(amount);
    }
  }

  public onTopUp(): void {
    this.cartService.changeTopUpAmount$(this.selectedAmountInCents()() ?? 0).subscribe();
    this.topUpEvents.emit({ type: 'Top up added' });
  }

  public remove(): void {
    const cartItemId = this.cartStoreSelector.topUpProductInCart()?.id;
    if (!cartItemId) {
      return;
    }
    this.cartService.removeItem$(cartItemId).subscribe();
    this.topUpEvents.emit({ type: 'Top up removed' });
  }

  public cancel() {
    this.topUpEvents.emit({ type: 'Cancelled' });
  }
}
